import { addEvent, removeEvent, triggerEvent } from '../utils/events';
import { addClasses, removeClasses } from '../utils/html';

export class Filter {

  /**
   * The filter file assigns open and close
   * functionality to provided elements. It also provides
   * toggle functionality around the "Apply" button
   * which closes the filter but can also be used to
   * call an action within the application developers code.
   *
   * @param  {string} filter               DOM element
   * @param  {string} openClickArea        Js hook of open area
   * @param  {string} remoteOpenClickArea  JS hook from content toggles filter
   * @param  {string} closeClickArea       JS hook of close area
   * @param  {string} applyFilterClickArea JS hook of action button
   * @return undefined
   *
   * @author  Barry Hassan
   */
  constructor(filter, openClickArea, remoteOpenClickArea, closeClickArea, applyFilterClickArea) {
    this.filter = filter;

    if (!this.isEverythingOk()) {
      return;
    }

    this.closeClickArea = filter.querySelector(closeClickArea);
    this.openClickArea = filter.querySelector(openClickArea);
    this.remoteOpenClickArea = filter.querySelector(remoteOpenClickArea);
    this.applyFilterClickArea = filter.querySelector(applyFilterClickArea);
    this.sidebarForm = filter.querySelector('form');

    this.isOpen = true;

    this.init();
  }

  /**
   * Assigns events to the close button
   * and the filter action button (Apply)
   * @return undefined
   */
  init() {
    if (this.closeClickArea) {
      addEvent(this.closeClickArea, 'click', this.close.bind(this));
    }
    if (this.applyFilterClickArea) {
      addEvent(this.applyFilterClickArea, 'click', this.apply.bind(this));
    }
    if (this.sidebarForm) {
      addEvent(this.sidebarForm, 'keyup', this.enableAction.bind(this));
      addEvent(this.sidebarForm, 'change', this.enableAction.bind(this));
    }
  }

  /**
   * Removes the disabled attribute
   * from the sidebar action
   * @return undefined
   */
  enableAction() {
    if (this.applyFilterClickArea) {
      this.applyFilterClickArea.removeAttribute('disabled');
      if (this.sidebarForm) {
        removeEvent(this.sidebarForm, 'keyup', this.enableAction.bind(this));
        removeEvent(this.sidebarForm, 'change', this.enableAction.bind(this));
      }
    }
  }

  /**
   * Adds the disabled attribute
   * to the sidebar action
   * @return undefined
   */
  disableAction() {
    if (this.applyFilterClickArea) {
      this.applyFilterClickArea.setAttribute('disabled', true);
      if (this.sidebarForm) {
        addEvent(this.sidebarForm, 'keyup', this.enableAction.bind(this));
        addEvent(this.sidebarForm, 'change', this.enableAction.bind(this));
      }
    }
  }

  /**
   * Opens the filter, applies classes
   * @param  {event} event click event
   * @return undefined
   */
  open(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.isOpen) {
      if (this.openClickArea || this.remoteOpenClickArea) {
        removeClasses(this.filter, [ 'is-compact' ]);
        removeEvent(this.openClickArea, 'click', this.open.bind(this));
        removeEvent(this.remoteOpenClickArea, 'click', this.open.bind(this));
      }
      this.isOpen = true;
    }
  }

  /**
   * Closes the filter, switches the functionality
   * of the filter to now open
   * @param  {event} event click event
   * @return undefined
   */
  close(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.isOpen) {
      if (this.closeClickArea) {
        addClasses(this.filter, [ 'is-compact' ]);
        removeEvent(this.closeClickArea, 'click', this.close.bind(this));
      }
      this.isOpen = false;
      if (this.openClickArea || this.remoteOpenClickArea) {
        addEvent(this.openClickArea, 'click', this.open.bind(this));
        addEvent(this.remoteOpenClickArea, 'click', this.open.bind(this));
      }
    }
  }

  /**
   * Toggles content button if present
   * to switch between icon classes
   * @param  {event} event click event
   * @return undefined
   */
  apply(event) {
    if (this.applyFilterClickArea) {
      const remoteIcon = this.filter.querySelector('.js-filter-panel__state');
      if (remoteIcon) {
        removeClasses(remoteIcon, [ 'icon-cme-filter' ]);
        addClasses(remoteIcon, [ 'fa', 'fa-check' ]);
      }
      this.close(event);
      removeEvent(this.closeClickArea, 'click', this.close.bind(this));
      triggerEvent(this.filter, 'apply.commonui.filter');
    }
  }

  destroy() {
    this.close();
  }

  /* eslint-disable no-console */
  isEverythingOk() {
    if (!this.filter) {
      console.warn('CommonUI - Filter - There\'s no filter found');
      return false;
    }
    return true;
  }
  /* eslint-enable no-console */
}

/**
 * Creates a new filter instance
 * @param  {Object} config
 */
export default function initFilter(config = {}) {
  const {
    selector = '.js-filter-panel',
    openSelector = '.js-filter-panel__open',
    remoteOpenClickArea = '.js-filter-panel__remote-open',
    closeSelector = '.js-filter-panel__close',
    applyFilterSelector = '.js-filter-panel__action',
  } = config;
  let filters = null;
  if (typeof selector === 'string') {
    for (const filter of document.querySelectorAll(selector)) {
      filters = new Filter(filter, openSelector, remoteOpenClickArea, closeSelector, applyFilterSelector);
    }
  }
  return filters;
}



// WEBPACK FOOTER //
// ./js/components/Filter.js